import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, inject, OnDestroy } from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { PopoverService, PopoversState } from '../../../services/popover.service';
import { SolPopoverComponent } from '../sol-popover/sol-popover.component';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { SolPopoversState } from './sol-popovers.state';
import { ShortcutService, SolShortcut, SolShortcutSubscription } from '../../../services/shortcut.service';
import { PopoverTools } from '../../../tools/popover-tools';

@Component({
  selector: 'sol-popovers',
  imports: [SolPopoverComponent, CdkTrapFocus],
  templateUrl: './sol-popovers.component.html',
  host: { 'data-shortcut-layer': 'popover' },
})
export class SolPopoversComponent extends AbstractComponent<SolPopoversState> implements OnDestroy {
  // Dependencies
  private readonly popoverService = inject(PopoverService);
  private readonly shortcutService = inject(ShortcutService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly document = inject(DOCUMENT);

  // Cache
  private shortcutSubscription?: SolShortcutSubscription;
  private scrollLocked = false;
  private readonly documentPointerDownListener = (event: Event) => this.onDocumentPointerDown(event);
  private readonly repositionListener = () => this.popoverService.reposition();
  private readonly scrollLockListener = (event: WheelEvent | TouchEvent | KeyboardEvent) => this.onScrollLock(event);

  // Computed
  protected readonly visible$ = this.compute(state => state.visible);
  protected readonly popovers$ = this.compute(state => state.popovers);

  constructor() {
    super(SolPopoversState.of());

    this.popoverService.watch(popoversState => this.updatePopovers(popoversState));
    this.document.addEventListener('pointerdown', this.documentPointerDownListener, true);
    this.document.addEventListener('scroll', this.repositionListener, { capture: true, passive: true });
    this.document.defaultView?.addEventListener('resize', this.repositionListener);
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('pointerdown', this.documentPointerDownListener, true);
    this.document.removeEventListener('scroll', this.repositionListener, true);
    this.document.defaultView?.removeEventListener('resize', this.repositionListener);
    this.releaseScrollLock();
    this.shortcutSubscription?.unsubscribe();
  }

  private updatePopovers(popoversState: PopoversState): void {
    this.update(state => state.withPopovers(popoversState.popovers));
    this.syncShortcutRegistration(popoversState.popovers);
    this.syncScrollLock(popoversState.popovers);
  }

  private onScrollLock(event: WheelEvent | TouchEvent | KeyboardEvent): void {
    if (this.containsPopoverTarget(event)) {
      return;
    }
    if (PopoverTools.scrollsPage(event)) {
      event.preventDefault();
    }
  }

  private containsPopoverTarget(event: Event): boolean {
    return event.composedPath().includes(this.host.nativeElement);
  }

  private onDocumentPointerDown(event: Event): void {
    const popovers = this.state.popovers;
    if (popovers.length === 0) {
      return;
    }
    const topPopover = popovers[popovers.length - 1];
    if (this.containsEventTarget(event, topPopover)) {
      return;
    }
    this.popoverService.clickOutside(topPopover.id);
  }

  private containsEventTarget(event: Event, popover: PopoversState['popovers'][number]): boolean {
    const path = event.composedPath();
    return this.isPopoverTarget(path, popover.id) || this.isClickInsideRefTarget(path, popover);
  }

  private isPopoverTarget(path: EventTarget[], id: string): boolean {
    const popover = this.host.nativeElement.querySelector(`[data-sol-popover-id="${id}"]`);
    return !!popover && path.includes(popover);
  }

  private isClickInsideRefTarget(path: EventTarget[], popover: PopoversState['popovers'][number]): boolean {
    return popover.options.clickInsideRefs.some(ref => path.includes(ref.nativeElement));
  }

  private syncScrollLock(popovers: PopoversState['popovers']): void {
    if (popovers.length > 0) {
      this.holdScrollLock();
    } else {
      this.releaseScrollLock();
    }
  }

  private holdScrollLock(): void {
    if (this.scrollLocked) {
      return;
    }
    this.scrollLocked = true;
    this.document.addEventListener('wheel', this.scrollLockListener, { capture: true, passive: false });
    this.document.addEventListener('touchmove', this.scrollLockListener, { capture: true, passive: false });
    this.document.addEventListener('keydown', this.scrollLockListener, { capture: true, passive: false });
  }

  private releaseScrollLock(): void {
    if (!this.scrollLocked) {
      return;
    }
    this.scrollLocked = false;
    this.document.removeEventListener('wheel', this.scrollLockListener, true);
    this.document.removeEventListener('touchmove', this.scrollLockListener, true);
    this.document.removeEventListener('keydown', this.scrollLockListener, true);
  }

  private syncShortcutRegistration(popovers: PopoversState['popovers']): void {
    if (popovers.length > 0) {
      if (!this.shortcutSubscription) {
        this.shortcutSubscription = this.shortcutService.register('sol-popovers', true, this.host, [
          { shortcut: SolShortcut.of('Escape'), action: () => this.onShortcutEscape() },
        ]);
      }
    } else {
      this.shortcutSubscription?.unsubscribe();
      this.shortcutSubscription = undefined;
    }
  }

  private onShortcutEscape(): boolean {
    const popovers = this.state.popovers;
    if (popovers.length === 0) return false;
    this.popoverService.clickOutside(popovers[popovers.length - 1].id);
    return true;
  }
}
